import { QueryClient } from "@tanstack/vue-query";
import { ApiError } from "@/shared/api-fetch";

/**
 * The one `QueryClient` this app runs on for the page's whole life — created explicitly and
 * exported as a singleton, the same shape `app/router.ts` exports `router`, so any module that
 * ends a session can clear it without being a component with `useQueryClient()` in scope.
 *
 * **Why this file exists at all:** `main.ts` used to call `app.use(VueQueryPlugin)` with no
 * options, which lets the plugin construct its own internal `QueryClient` that nothing outside a
 * component's `setup()` can reach. That is what let `docs/UNOBSERVED.md` §1.3 happen twice over —
 * `features/auth/session.ts`'s `logout()` had no client to clear, and nothing else did either.
 * `main.ts` now passes THIS instance to the plugin, so the instance a component reads via
 * `useQueryClient()` and the instance `session.ts`'s `clear()` calls `.clear()` on are the same
 * object.
 *
 * **The `defaultOptions` below are chosen, not inherited from the library.** A bare `new
 * QueryClient()` runs every query at `retry: 3` with exponential backoff, `refetchOnWindowFocus:
 * true` and `staleTime: 0` — none of which fit a reconciliation screen backed by a service that
 * answers with a CODE, not a transient network blip, when a request cannot succeed.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * A FUNCTION, never a flat number. `ApiError.code`/`.isUpstream` is what CLAUDE.md and
       * `global-constraints.md` both make normative to branch on — several codes share the same
       * HTTP status, so the status alone cannot tell "the dependency did not answer, try again"
       * apart from "this will never succeed, stop asking."
       *
       * `ROLE_FORBIDDEN`, a 404, or a validation refusal (`ApiError.isUpstream` is `false` for all
       * three) are refused on the FIRST attempt — the answer does not change on a second or third
       * try, and the old flat `retry: 3` spent about seven seconds of "loading" retrying an outcome
       * that was already final before the first response landed.
       *
       * Only `ApiError.isUpstream` (`code === "UPSTREAM_UNAVAILABLE"` — "a dependency did not
       * answer," see `shared/api-fetch.ts`'s own doc comment on that getter) is worth a second
       * look, and even then at most TWICE, not three times: a genuinely down dependency on a
       * reconciliation screen should surface its own error state quickly enough that a manager
       * reaches for a different tool rather than staring at "Loading…" through a third backoff.
       * A non-`ApiError` failure (a thrown `TypeError`, a parse failure) is treated the same as a
       * non-upstream `ApiError` — never retried — because nothing about it says "try the exact same
       * request again and expect a different outcome."
       */
      retry: (failureCount, error) => {
        if (!(error instanceof ApiError) || !error.isUpstream) return false;
        return failureCount < 2;
      },
      /**
       * A reconciliation screen re-issuing every one of its mounted queries — several at once on
       * `/day` — each time an operator alt-tabs back is load with no reason behind it. A manual
       * refresh is always available; a surprise refetch in the middle of a cash count is not
       * wanted, because the figures on screen changing out from under a manager mid-count is
       * exactly the kind of surprise this screen exists to prevent.
       */
      refetchOnWindowFocus: false,
      /**
       * 30 seconds: long enough that switching between workspaces (Kiosks → Cage → Business day
       * and back) does not re-fetch everything each time, short enough that a stale figure cannot
       * sit unnoticed on a reconciliation screen through an entire shift. Neither extreme fits this
       * product — `staleTime: 0` refetches on every remount for no reason, and a long stale time
       * risks a manager reconciling against numbers that quietly stopped being current.
       */
      staleTime: 30_000,
    },
  },
});
